/**
 * The inventory and evolution, pure. An item is bought into the inventory
 * (game/economy.ts buyShopItem), carried, and then used on a dinosaur the
 * player picks, which consumes it. Nothing here touches the DOM.
 *
 * Evolving is progression, not replacement: the tier keeps its id, level,
 * pet name, doublers and achievements. Only the species name, the sprites
 * and the base rate change.
 */

import {
  DINOS,
  EVOLVED_EPS_MULTIPLIER,
  EVOLVE_MIN_LEVEL,
  aOrAn,
  spliceReaches,
} from '../data/dinos';
import type { DinoDef } from '../data/dinos';
import { ITEMS_BY_ID } from '../data/items';
import { SHOP_ITEMS_BY_ID } from '../data/shop';
import type { ItemDef } from '../data/items';
import type { GameState } from './state';

// ---------------------------------------------------------------------------
// Holding items

export function countOf(state: GameState, itemId: string): number {
  return state.inventory[itemId] ?? 0;
}

export function addItem(state: GameState, itemId: string, n = 1): void {
  if (n <= 0) return;
  state.inventory[itemId] = countOf(state, itemId) + n;
}

/** Every item held, in the data file's order, with its count. */
export function heldItems(state: GameState): { def: ItemDef; count: number }[] {
  const out: { def: ItemDef; count: number }[] = [];
  for (const def of ITEMS_BY_ID.values()) {
    const count = countOf(state, def.id);
    if (count > 0) out.push({ def, count });
  }
  return out;
}

/**
 * Whether the Inventory has ever had anything in it. Every purchase is kept
 * in state.shop, repeats included, so the tab stays once it has appeared
 * instead of vanishing the moment the last splice is used.
 */
export function isInventoryUnlocked(state: GameState): boolean {
  if (heldItems(state).length > 0) return true;
  // Having evolved anything means an item passed through, whether or not the
  // purchase is on record.
  if (state.evolved.length > 0) return true;
  return state.shop.some((id) => SHOP_ITEMS_BY_ID.get(id)?.effect.kind === 'grantItem');
}

// ---------------------------------------------------------------------------
// Evolving

export function isEvolved(def: DinoDef, state: GameState): boolean {
  return state.evolved.includes(def.id);
}

/** The species name to show: the evolved form's once evolved, else the tier's. */
export function speciesName(def: DinoDef, state: GameState): string {
  return isEvolved(def, state) ? def.evolved.name : def.name;
}

/** The flavour line to show, likewise. */
export function speciesFlavor(def: DinoDef, state: GameState): string {
  return isEvolved(def, state) ? def.evolved.flavor : def.flavor;
}

/** The sprite id to draw: the evolved form's art once evolved. */
export function spriteIdFor(def: DinoDef, state: GameState): string {
  return isEvolved(def, state) ? def.evolved.id : def.id;
}

/** Multiplier on this tier's base rate from having evolved (1 if not). */
export function evolutionMultiplier(def: DinoDef, state: GameState): number {
  return isEvolved(def, state) ? EVOLVED_EPS_MULTIPLIER : 1;
}

/**
 * Rewrites any evolved tier's base species name in a string to its evolved
 * one, so the templated upgrade and achievement names ("Cozy Ankylosaurus
 * nests") follow an evolution without the data files needing the state.
 * An article in front of the name is redone for the new one: "Hatch an
 * Ankylosaurus" becomes "Hatch a Sauropelta".
 */
export function withSpeciesNames(text: string, state: GameState): string {
  if (state.evolved.length === 0) return text;
  // The upgrade panel asks for the same eighty-odd texts on every refresh,
  // and the answer only changes when something evolves: keep the results for
  // the current evolved set. Building each pattern afresh on every call made
  // this the heaviest thing in a late-game frame.
  const key = state.evolved.join('|');
  if (key !== renamedFor) {
    renamedFor = key;
    renamed.clear();
  }
  const hit = renamed.get(text);
  if (hit !== undefined) return hit;
  let out = text;
  for (const def of DINOS) {
    if (!isEvolved(def, state)) continue;
    const next = def.evolved.name;
    out = out.replace(namePattern(def), (_m, article?: string) =>
      article ? `${aOrAn(next)} ${next}` : next,
    );
  }
  renamed.set(text, out);
  return out;
}

let renamedFor = '';
const renamed = new Map<string, string>();
const namePatterns = new Map<string, RegExp>();

/**
 * A tier's base name with an optional article before it, compiled once. One
 * pass, with the article optional: two passes (the article's, then the bare
 * name's) renamed the name again inside an evolved name that contains it, and
 * "a Tyrannosaurus" became "a Tyrannosaurus Rex Alpha Rex Alpha". The article
 * is whole-word, so "Mega Gallimimus" is not read as "a Gallimimus".
 */
function namePattern(def: DinoDef): RegExp {
  let pattern = namePatterns.get(def.id);
  if (!pattern) {
    const name = def.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    pattern = new RegExp(`(\\b(?:a|an) )?${name}`, 'g');
    namePatterns.set(def.id, pattern);
  }
  return pattern;
}

export type Eligibility = { ok: true } | { ok: false; reason: string };

/** Why no dinosaur can take `item` right now, for the holding bar. */
export function noTargetHint(item: ItemDef, state: GameState): string {
  const reach = DINOS.filter((def) => spliceReaches(item.grade, def));
  if (reach.every((def) => isEvolved(def, state))) {
    return 'Every dinosaur it can reach has already evolved.';
  }
  return `No dinosaur is ready for it yet. Raise one to level ${EVOLVE_MIN_LEVEL}.`;
}

/**
 * Whether `item` may be used on `def` right now, with a reason the tooltip
 * can show when it may not.
 */
export function eligibility(item: ItemDef, def: DinoDef, state: GameState): Eligibility {
  if (!spliceReaches(item.grade, def)) {
    const last = DINOS.filter((d) => spliceReaches(item.grade, d)).at(-1);
    return { ok: false, reason: `This splice only reaches ${last?.name ?? 'the early tiers'}` };
  }
  if (isEvolved(def, state)) return { ok: false, reason: 'Already evolved' };
  const level = state.dinos[def.id] ?? 0;
  if (level === 0) return { ok: false, reason: 'Not hatched yet' };
  if (level < EVOLVE_MIN_LEVEL) {
    return { ok: false, reason: `Needs level ${EVOLVE_MIN_LEVEL} (currently ${level})` };
  }
  return { ok: true };
}

/** Any dinosaur this item could be used on right now. */
export function eligibleTargets(item: ItemDef, state: GameState): DinoDef[] {
  return DINOS.filter((def) => eligibility(item, def, state).ok);
}

/** An item held that can be used on this dinosaur, cheapest grade first, or null. */
export function usableItemFor(def: DinoDef, state: GameState): ItemDef | null {
  for (const { def: item } of heldItems(state)) {
    if (eligibility(item, def, state).ok) return item;
  }
  return null;
}

/**
 * Uses an item on a dinosaur: evolves the tier and consumes one of the item.
 * Returns whether it did.
 */
export function useItemOn(item: ItemDef, def: DinoDef, state: GameState): boolean {
  if (countOf(state, item.id) <= 0) return false;
  if (!eligibility(item, def, state).ok) return false;
  state.inventory[item.id] = countOf(state, item.id) - 1;
  if (state.inventory[item.id] === 0) delete state.inventory[item.id];
  state.evolved.push(def.id);
  return true;
}
